// Quick date-range presets shared by the Payment Processing screens (To Process +
// Processed). Pure functions — kept free of react so they're unit-testable.
//
// Every range is computed in businessTimeZone (see project timezone rules — NEVER the
// operator's browser timezone), and .startOf('day') zeroes the time-of-day so the
// Calendar picker (which defaults to timezone="UTC") displays the same calendar date
// we actually query.
import dayjs from 'dayjs'
import { businessTimeZone } from '../../../utils/date'

export const CUSTOM_PRESET_KEY = 'custom'

// How far back the Overdue tab reaches. A scheduled payment that was never processed stays
// `scheduled` indefinitely, so an unbounded `from` could surface years-old stranded records and
// bury the real weekend/missed-day backlog. 90 days covers any realistic operational gap
// (weekends, holidays, extended operator absence) while capping the query; anything older is
// reachable via the Custom range.
export const OVERDUE_LOOKBACK_DAYS = 90

// How far FORWARD past today the Upcoming tab reaches, so weekend/holiday-due payments can be
// processed EARLY instead of waiting for each day to arrive. 2 days covers a Friday's weekend
// (today+1..+2 = Sat/Sun); widen it here for a longer lead. Single source of truth — the
// Upcoming range and its tab label both derive from it.
export const UPCOMING_LOOKAHEAD_DAYS = 2

// The three due-date populations are DELIBERATELY separate tabs, never one merged range.
//
// Until 2026-07-28 the default was a single `today - 90 .. today + 3` window: overdue backlog,
// today, and future-dated payments rendered as one undifferentiated list. "Select all clean"
// swept the future-dated rows in with today's and 104 payments due 07/29-07/31 were charged on
// 07/28 (~$30.4k, voided by hand at the gateway). Keeping the populations in separate ranges is
// the structural half of the fix; the explicit future-charge confirmation in the approve flow
// (see DailyProcessing/ConfirmDrawer) is the other half, and vlg-server's `not_yet_due` guard
// is the backstop.
export const DEFAULT_DAILY_PRESET_KEY = 'today'
export const OVERDUE_PRESET_KEY = 'overdue'
export const UPCOMING_PRESET_KEY = 'todayUpcoming'

export const DAILY_PRESETS = [
  { key: DEFAULT_DAILY_PRESET_KEY, label: 'Today' },
  { key: OVERDUE_PRESET_KEY, label: 'Overdue' },
  { key: UPCOMING_PRESET_KEY, label: `Today + Upcoming ${UPCOMING_LOOKAHEAD_DAYS} Days` },
  { key: 'thisWeek', label: 'This Week' },
  { key: CUSTOM_PRESET_KEY, label: 'Custom' },
]

export const PROCESSED_PRESETS = [
  { key: 'today', label: 'Today' },
  { key: 'thisWeek', label: 'This Week' },
  { key: 'last30', label: 'Last 30 Days' },
  { key: CUSTOM_PRESET_KEY, label: 'Custom' },
]

/**
 * The {from, to} dayjs range for a preset key, or null for 'custom'/unknown keys —
 * 'custom' means "whatever the operator picked in the calendars", so there is no
 * range to impose.
 */
export const getPresetRange = (key) => {
  const today = dayjs().tz(businessTimeZone).startOf('day')
  switch (key) {
    // Default To Process range: the day's own work, nothing else.
    case DEFAULT_DAILY_PRESET_KEY:
      return { from: today, to: today }
    // The missed/weekend backlog, ending STRICTLY BEFORE today so this tab can never
    // act on something that isn't already past due.
    case OVERDUE_PRESET_KEY:
      return { from: today.subtract(OVERDUE_LOOKBACK_DAYS, 'day'), to: today.subtract(1, 'day') }
    // The deliberate "process the weekend early on Friday" view. Starts at today (never
    // reaches into the backlog) and the future-dated rows it surfaces are charge-gated
    // behind the confirm drawer's explicit opt-in.
    case UPCOMING_PRESET_KEY:
      return { from: today, to: today.add(UPCOMING_LOOKAHEAD_DAYS, 'day') }
    case 'thisWeek':
      return { from: today.startOf('week'), to: today.endOf('week').startOf('day') }
    case 'last30':
      return { from: today.subtract(29, 'day'), to: today }
    default:
      return null
  }
}

/**
 * Today's BUSINESS calendar day (America/New_York) as a 'YYYY-MM-DD' key — the reference
 * point overdue/future bucketing compares row due-dates against. Business time, never the
 * operator's browser timezone (see project timezone rules), so "overdue" and "not yet due"
 * mean the same day boundary for staff in New York, Los Angeles, or Kyiv.
 */
export const todayBusinessKey = () => dayjs().tz(businessTimeZone).format('YYYY-MM-DD')
